import React, { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '../../../../../elements/button'
import CustomAgGrid from '../../../../../components/CustomAgGrid'
import { useExperimentApi } from '../../../../../store/api/hooks/experimentApiHook'
import { AgGridReact } from 'ag-grid-react'
import { SelectedDataTableType } from '../../../../../_config/types'
import Loader from '../../../../../components/loader'
import { cellRenderer } from '../../../../../components/cellRenderer'
import { normalizeRowValues, trackEvent } from '../../../../../_common/functions'
import i18n from '../../../../../i18n'

interface DataTableModalProps {
    _ref: any
    literal: string
    table: SelectedDataTableType | null
    onContinue: () => void
    onCancel: () => void
    forceOpen?: boolean
}

const DataTableModal: React.FC<DataTableModalProps> = ({ _ref, literal, table, onContinue, onCancel, forceOpen }) => {
    const { t } = useTranslation()
    const dataTableGrid = useRef<AgGridReact>(null)
    const experimentApi = useExperimentApi()
    const [rowData, setRowData] = useState<any[] | null>(null)
    const [columnDefs, setcolumnDefs] = useState<[]>([])
    const [open, setOpen] = useState<boolean>(forceOpen ?? false)
    const [previewDataError, setPreviewDataError] = useState<string | null>(null)

    const handlePreviewDataError = (error: any) => {
        setRowData([])
        //setPreviewDataError(typeof error === 'string' ? error : (error?.message ?? 'Unknown error'))
        setPreviewDataError('error')
    }

    const getDesignTableData = () => {
        setRowData(null)
        setPreviewDataError(null)

        const payload: any = {
            page: 1,
            per_page: 10,
        }

        if (table?.table.userTable) {
            payload.query = 'SELECT * FROM ' + table?.table?.name
            payload.table_name = ''
        } else {
            payload.query = ''
            payload.table_name = table?.table?.name
        }

        experimentApi.getmeasurementdata(
            payload,
            (data: any) => {
                const rawRows: any[] = (data?.data?.result ?? []).map((item: any) => item)
                const result: any[] = rawRows.filter((row: any) => row && typeof row === 'object').map(normalizeRowValues)

                if (result.length === 0) {
                    setcolumnDefs([])
                    setRowData([])
                    return
                }

                const columnDefs: any = Object.keys(result[0]).map((key: any) => ({
                    headerName: key,
                    field: key,
                    filter: false,
                    sortable: false,
                    suppressMenu: true,
                    cellStyle: {
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    },
                    cellRenderer,
                }))
                setcolumnDefs(columnDefs)
                setRowData(result)
            },
            handlePreviewDataError,
        )
    }

    useEffect(() => {
        if (table && open) {
            getDesignTableData()
        }
    }, [table, open])

    useEffect(() => {
        if (_ref?.current) {
            const originalShowModal = _ref.current.showModal
            _ref.current.showModal = function () {
                setOpen(true)
                originalShowModal?.call(this)
            }

            _ref.current.addEventListener('cancel', () => {
                setRowData(null)
                setOpen(false)
            })
        }
    }, [])

    const noRowsOverlayComponent = () => {
        return previewDataError ? (
            <p className="text-red-700 text-center">
                Error: Table not found or access denied — the table either does not exist or the user lacks the necessary permissions.
                <br />
                Please change the table and try again.
            </p>
        ) : null
    }

    return (
        <dialog ref={_ref} className="modal" data-testid="datatable-modal">
            {open && (
                <>
                    <div className="modal-box w-11/12 max-w-5xl" data-testid="datatable-modal-box">
                        <p className="font-bold text-center text-[1.333rem] leading-8" data-testid="modal-title">
                            {t('Data Preview')}
                        </p>
                        <div data-testid="datatable-grid-section">
                            {rowData ? (
                                <CustomAgGrid
                                    key={'dataTable' + i18n.language}
                                    gridRef={dataTableGrid}
                                    rowData={rowData}
                                    columnDefs={columnDefs}
                                    defaultColDef={{ sortable: true, filter: true }}
                                    theme="light"
                                    pagination={false}
                                    paginationPageSize={5}
                                    paginationPageSizeSelector={[5, 10, 20, 50]}
                                    style={{ height: 350, width: '100%' }}
                                    gridId="summmyGrid"
                                    rowHeight={50}
                                    alternativeRowColor={'#E5B61112'}
                                    noRowsOverlayComponent={noRowsOverlayComponent}
                                />
                            ) : (
                                <Loader data-testid="datatable-loader" />
                            )}
                        </div>
                        <div className="flex justify-center gap-4 pt-4">
                            <Button
                                theme="secondary"
                                size="xs"
                                onClick={() => {
                                    trackEvent('wizard_data_table_modal_cancelled', {})
                                    onCancel()
                                }}
                                data-testid="cancel-btn"
                            >
                                {t('Cancel')}
                            </Button>
                            <Button
                                theme="primary"
                                size="xs"
                                disabled={rowData == null || !!previewDataError}
                                onClick={() => {
                                    trackEvent('data_table_selected', { table_name: table?.table?.name })
                                    onContinue()
                                }}
                                data-testid="continue-btn"
                            >
                                {t('Continue')}
                            </Button>
                        </div>
                    </div>
                    <form method="dialog" className="modal-backdrop" data-testid="datatable-backdrop">
                        <button type="button">{t('close')}</button>
                    </form>
                </>
            )}
        </dialog>
    )
}

export default DataTableModal
